/**
 * Region boundary conditions — hashed from shared coordinates, never
 * communicated (spec §3.3). The canonical edge identity is the SORTED pair
 * of packed cell ids of the two cells sharing the edge: exact integer
 * arithmetic, no float-quantization boundary risk. The neighbor is found
 * geometrically: probe a point just across the edge and classify it —
 * faceDir() happily normalizes |u| > 1, so probes roll onto adjacent faces
 * and cross-face adjacency needs no topology tables.
 */
import { faceDir } from "../cubesphere.js";
import { cellsPerFace, dirToCell, packCell, type CellId } from "./cells.js";
import { hashInts, h01 } from "./hash.js";

export const SIDE_NEG_U = 0;
export const SIDE_POS_U = 1;
export const SIDE_NEG_V = 2;
export const SIDE_POS_V = 3;

export interface EdgePort {
  /** Position along the edge, (0,1), measured from canonical endpoint A. */
  t: number;
  /** Entry heading (radians) relative to the A→B edge direction. */
  heading: number;
}

/** Face-local uv of the midpoint of a cell side, plus the outward uv step. */
function sideMidUV(n: number, cell: CellId, side: number): { u: number; v: number; du: number; dv: number } {
  const w = 2 / n; // cell width in uv
  const cu = -1 + w * (cell.x + 0.5);
  const cv = -1 + w * (cell.y + 0.5);
  switch (side) {
    case SIDE_NEG_U: return { u: cu - w / 2, v: cv, du: -w, dv: 0 };
    case SIDE_POS_U: return { u: cu + w / 2, v: cv, du: w, dv: 0 };
    case SIDE_NEG_V: return { u: cu, v: cv - w / 2, du: 0, dv: -w };
    default:         return { u: cu, v: cv + w / 2, du: 0, dv: w };
  }
}

/**
 * The cell on the other side of `side`. Probe 0.25 cell-widths beyond the
 * edge midpoint — deep enough into the neighbor to classify robustly, close
 * enough to the edge to stay inside it even under corner distortion.
 */
export function edgeNeighbor(radius: number, cell: CellId, side: number): CellId {
  const n = cellsPerFace(radius, cell.level);
  const m = sideMidUV(n, cell, side);
  const probe: number[] = [0, 0, 0];
  faceDir(cell.face, m.u + m.du * 0.25, m.v + m.dv * 0.25, probe);
  return dirToCell(radius, cell.level, probe[0], probe[1], probe[2]);
}

/** Canonical uint32 edge key — identical computed from either neighbor. */
export function edgeKey(planetHash: number, radius: number, cell: CellId, side: number): number {
  const a = packCell(cell);
  const b = packCell(edgeNeighbor(radius, cell, side));
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return hashInts(planetHash, cell.level, lo, hi, 0x0edce, 0);
}

/**
 * Canonical endpoints of a cell side as unit dirs. A is the lexicographically
 * smaller of the two corner dirs (1e-9 tolerance — corners of one edge are
 * macroscopically distinct, so the tolerance never decides). Both neighbors
 * compute the same corners (within float ulps) and thus the same A/B.
 */
export function edgeEndpoints(radius: number, cell: CellId, side: number, outA: number[] | Float64Array, outB: number[] | Float64Array): void {
  const n = cellsPerFace(radius, cell.level);
  const w = 2 / n;
  const u0 = -1 + w * cell.x;
  const v0 = -1 + w * cell.y;
  let p: [number, number], q: [number, number];
  switch (side) {
    case SIDE_NEG_U: p = [u0, v0]; q = [u0, v0 + w]; break;
    case SIDE_POS_U: p = [u0 + w, v0]; q = [u0 + w, v0 + w]; break;
    case SIDE_NEG_V: p = [u0, v0]; q = [u0 + w, v0]; break;
    default:         p = [u0, v0 + w]; q = [u0 + w, v0 + w]; break;
  }
  const c1: number[] = [0, 0, 0];
  const c2: number[] = [0, 0, 0];
  faceDir(cell.face, p[0], p[1], c1);
  faceDir(cell.face, q[0], q[1], c2);
  // Lexicographic order with tolerance so both neighbors pick the same A.
  const EPS = 1e-9;
  let firstIsA = false;
  for (let i = 0; i < 3; i++) {
    if (c1[i] < c2[i] - EPS) { firstIsA = true; break; }
    if (c1[i] > c2[i] + EPS) { firstIsA = false; break; }
  }
  const A = firstIsA ? c1 : c2;
  const B = firstIsA ? c2 : c1;
  outA[0] = A[0]; outA[1] = A[1]; outA[2] = A[2];
  outB[0] = B[0]; outB[1] = B[1]; outB[2] = B[2];
}

/**
 * Arterial entry/exit ports on a cell side — 1–2 ports with positions along
 * the canonical A→B direction and entry headings. Pure function of the edge
 * key, so both neighbors produce byte-identical arrays.
 */
export function edgePorts(planetHash: number, radius: number, cell: CellId, side: number): EdgePort[] {
  const k = edgeKey(planetHash, radius, cell, side);
  const count = 1 + (hashInts(k, 1) & 1);
  const ports: EdgePort[] = [];
  for (let i = 0; i < count; i++) {
    const t = 0.15 + 0.7 * h01(hashInts(k, 2, i));
    const heading = (h01(hashInts(k, 3, i)) - 0.5) * (Math.PI / 3);
    ports.push({ t, heading });
  }
  ports.sort((a, b) => a.t - b.t);
  return ports;
}
